<template>
  <v-card outlined class="mb-4">
    <v-card-title class="refund-section-title">{{ $t('refund.articlesAndFees') }}</v-card-title>
    <v-divider></v-divider>

    <!-- Articles -->
    <v-list two-line>
      <div v-for="(item, index) in items" :key="`item-${index}`">
        <v-list-item class="py-2">
          <v-list-item-avatar tile size="64" class="refund-item-avatar">
            <v-img :src="item.product_image_link || defaultImage" contain></v-img>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">{{ item.product_title }}</v-list-item-title>
            <v-row no-gutters class="refund-item-attributes mt-1">
              <v-col cols="12" sm="6">
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.sku') }} :</span>
                  {{ item.product_sku || '-' }}
                </div>
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.size') }} :</span>
                  {{ item.product_size || '-' }}
                </div>
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.price') }} :</span>
                  <span
                    v-if="hasDiscount(item) && isAmountDefined(item.product_default_price)"
                    class="refund-price-struck"
                  >
                    {{ item.product_default_price | formatPrice(currency) }} {{ currencyCode }}
                  </span>
                  <span v-if="isAmountDefined(item.product_sale_price)" class="refund-price-current">
                    {{ item.product_sale_price | formatPrice(currency) }} {{ currencyCode }}
                  </span>
                  <span v-else class="refund-price-current">-</span>
                </div>
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.lineAmount') }} :</span>
                  <span v-if="isAmountDefined(lineRefundAmount(item))" class="font-weight-medium">
                    {{ lineRefundAmount(item) | formatPrice(currency) }} {{ currencyCode }}
                    {{ $t('refund.taxIncludedShort') }}
                  </span>
                  <span v-else class="font-weight-medium">-</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.barcode') }} :</span>
                  {{ item.barcode || '-' }}
                </div>
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.color') }} :</span>
                  {{ item.product_color || '-' }}
                </div>
                <div class="refund-attr">
                  <span class="refund-attr-label">{{ $t('refund.refundedQuantity') }} :</span>
                  {{ item.refunded_quantity || 0 }}
                </div>
              </v-col>
            </v-row>
          </v-list-item-content>
        </v-list-item>
        <v-divider></v-divider>
      </div>
    </v-list>

    <!-- Sous-total articles -->
    <div class="refund-subtotal-row">
      <span class="refund-subtotal-label">{{ $t('refund.subtotalArticles') }}</span>
      <span class="refund-subtotal-value">
        {{ articlesSubtotal | formatPrice(currency) }} {{ currencyCode }} {{ $t('refund.taxIncludedShort') }}
      </span>
    </div>

    <!-- Frais -->
    <template v-if="fees.length">
      <v-divider></v-divider>
      <div class="refund-fees-header">
        <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
        {{ $t('refund.fees') }}
      </div>
      <v-simple-table dense class="refund-fees-table">
        <thead>
          <tr>
            <th scope="col">{{ $t('refund.feeLabel') }}</th>
            <th scope="col">{{ $t('refund.feeType') }}</th>
            <th scope="col" class="text-right">{{ $t('refund.amountWithoutTax') }}</th>
            <th scope="col" class="text-right">{{ $t('refund.amountWithTax') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fee, index) in fees" :key="`fee-${index}`">
            <td>{{ fee.label }}</td>
            <td>
              <v-chip
                x-small
                :color="feeTypeBackgroundColor(fee.type)"
                :text-color="feeTypeTextColor(fee.type)"
              >
                {{ feeTypeLabel(fee.type) }}
              </v-chip>
            </td>
            <td class="text-right" :class="amountClass(fee.amount_exclude_tax)">
              <template v-if="isAmountDefined(fee.amount_exclude_tax)">
                {{ signedAmount(fee.amount_exclude_tax) }}
              </template>
              <template v-else>-</template>
            </td>
            <td class="text-right" :class="amountClass(fee.amount)">
              {{ signedAmount(fee.amount) }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Sous-total frais -->
      <div class="refund-subtotal-row">
        <span class="refund-subtotal-label">{{ $t('refund.subtotalFees') }}</span>
        <span class="refund-subtotal-value" :class="amountClass(feesSubtotal)">
          {{ feesSubtotal | formatPrice(currency) }} {{ currencyCode }} {{ $t('refund.taxIncludedShort') }}
        </span>
      </div>
    </template>

    <v-divider></v-divider>

    <!-- Total général -->
    <div class="refund-total-row">
      <span class="refund-total-label">{{ $t('refund.totalRefunded') }}</span>
      <div class="text-right">
        <div class="refund-total-ttc">
          {{ grandTotalWithTax | formatPrice(currency) }} {{ currencyCode }} {{ $t('refund.taxIncludedShort') }}
        </div>
        <div v-if="isAmountDefined(grandTotalWithoutTax)" class="refund-total-ht">
          {{ grandTotalWithoutTax | formatPrice(currency) }} {{ currencyCode }} {{ $t('refund.taxExcludedShort') }}
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
  import {
    getCurrencyCode,
    getRefundFeeTypeBackgroundColor,
    getRefundFeeTypeTextColor,
    isAmountDefined,
  } from '../../helpers/refundHelper'

  export default {
    name: 'RefundArticlesFees',
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      fees: {
        type: Array,
        default: () => [],
      },
      currency: {
        type: Object,
        default: null,
      },
      amountWithTax: {
        type: Number,
        default: null,
      },
      amountWithoutTax: {
        type: Number,
        default: null,
      },
    },
    data() {
      return {
        defaultImage: require('@/assets/img/products/product_default.png'),
      }
    },
    computed: {
      currencyCode() {
        return getCurrencyCode(this.currency)
      },
      articlesSubtotal() {
        return this.items.reduce((sum, item) => {
          const amount = this.lineRefundAmount(item)
          return sum + (isAmountDefined(amount) ? amount : 0)
        }, 0)
      },
      feesSubtotal() {
        return this.fees.reduce((sum, fee) => sum + (fee.amount || 0), 0)
      },
      feesSubtotalWithoutTax() {
        // Tax-excluded fee amount is optional: if any fee is missing it, the HT subtotal is
        // incomplete and therefore unavailable (null) rather than a misleading partial sum.
        if (this.fees.some((fee) => !isAmountDefined(fee.amount_exclude_tax))) {
          return null
        }
        return this.fees.reduce((sum, fee) => sum + fee.amount_exclude_tax, 0)
      },
      articlesSubtotalWithoutTax() {
        // Same rule as fees: a single article without a tax-excluded line amount makes the HT
        // subtotal unavailable (null) instead of summing the others into a misleading total.
        if (this.items.some((item) => !isAmountDefined(this.lineRefundAmountWithoutTax(item)))) {
          return null
        }
        return this.items.reduce((sum, item) => sum + this.lineRefundAmountWithoutTax(item), 0)
      },
      grandTotalWithTax() {
        if (isAmountDefined(this.amountWithTax)) {
          return this.amountWithTax
        }
        return this.articlesSubtotal + this.feesSubtotal
      },
      grandTotalWithoutTax() {
        if (isAmountDefined(this.amountWithoutTax)) {
          return this.amountWithoutTax
        }
        // Both subtotals must be available: a single missing HT amount (article or fee) makes the
        // HT total incalculable, so return null rather than coercing null to 0 into a wrong total.
        if (!isAmountDefined(this.articlesSubtotalWithoutTax) || !isAmountDefined(this.feesSubtotalWithoutTax)) {
          return null
        }
        return this.articlesSubtotalWithoutTax + this.feesSubtotalWithoutTax
      },
    },
    methods: {
      isAmountDefined,
      /**
       * Refunded line amount, taxes included. `row_total_price` is the item's total in its
       * original order/return, NOT the refunded amount — so it is recomputed from the unit
       * sale price times the refunded quantity. Returns null when either input is missing.
       *
       * @param {Object} item - A refunded item.
       * @returns {number|null} The refunded line amount (tax included), or null.
       */
      lineRefundAmount(item) {
        const unitPrice = item.product_sale_price
        const quantity = item.refunded_quantity
        if (!isAmountDefined(unitPrice) || !isAmountDefined(quantity)) {
          return null
        }
        return unitPrice * quantity
      },
      /**
       * Refunded line amount, taxes excluded — same recomputation as lineRefundAmount but
       * from the tax-excluded unit sale price.
       *
       * @param {Object} item - A refunded item.
       * @returns {number|null} The refunded line amount (tax excluded), or null.
       */
      lineRefundAmountWithoutTax(item) {
        const unitPrice = item.product_sale_price_exclude_tax
        const quantity = item.refunded_quantity
        if (!isAmountDefined(unitPrice) || !isAmountDefined(quantity)) {
          return null
        }
        return unitPrice * quantity
      },
      hasDiscount(item) {
        return (
          item.product_default_price !== undefined &&
          item.product_sale_price !== undefined &&
          item.product_default_price !== item.product_sale_price
        )
      },
      feeTypeLabel(type) {
        const labels = {
          return: this.$t('refund.feeTypeReturn'),
          transport: this.$t('refund.feeTypeTransport'),
          other: this.$t('refund.feeTypeOther'),
        }
        return labels[type] ?? type
      },
      feeTypeBackgroundColor: getRefundFeeTypeBackgroundColor,
      feeTypeTextColor: getRefundFeeTypeTextColor,
      amountClass(amount) {
        if (amount > 0) {
          return 'refund-amount-positive'
        }
        if (amount < 0) {
          return 'refund-amount-negative'
        }
        return ''
      },
      signedAmount(amount) {
        const sign = amount > 0 ? '+' : ''
        const formatted = this.$options.filters.formatPrice(amount, this.currency)
        return `${sign}${formatted} ${this.currencyCode}`
      },
    },
  }
</script>

<style scoped>
  .refund-section-title {
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
  }
  .refund-item-avatar {
    border: 1px solid rgba(0, 0, 0, 0.12);
  }
  .refund-item-attributes {
    font-size: 0.85rem;
  }
  .refund-attr {
    padding: 1px 0;
  }
  .refund-attr-label {
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.55);
    margin-right: 4px;
  }
  .refund-price-struck {
    text-decoration: line-through;
    color: rgba(0, 0, 0, 0.45);
    margin-right: 4px;
  }
  .refund-price-current {
    font-weight: 500;
  }
  .refund-fees-header {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
  }
  .refund-fees-table th {
    text-transform: uppercase;
    font-size: 0.7rem !important;
    letter-spacing: 0.04em;
  }
  .refund-subtotal-row,
  .refund-total-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 10px 16px;
    /* Long labels (e.g. German) next to the amount can collide on narrow screens; let the value
       wrap onto its own line while margin-left keeps it flush right. */
    flex-wrap: wrap;
    gap: 2px 12px;
  }
  .refund-subtotal-value,
  .refund-total-row .text-right {
    margin-left: auto;
    text-align: right;
  }
  .refund-subtotal-row {
    background-color: rgba(0, 0, 0, 0.02);
  }
  .refund-subtotal-label,
  .refund-total-label {
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
  }
  .refund-subtotal-value {
    font-weight: 600;
  }
  .refund-total-ttc {
    font-weight: 700;
    font-size: 1.1rem;
    color: var(--v-primary-base);
  }
  .refund-total-ht {
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.6);
  }
  /* !important: override Vuetify's v-simple-table cell text color, which otherwise wins over this scoped class. */
  .refund-amount-positive {
    color: #2e7d32 !important;
  }
  .refund-amount-negative {
    color: #c62828 !important;
  }
</style>
